import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  analyticsEnabled,
  getAnalyticsConsent,
  setAnalyticsConsent,
} from "@/lib/analytics";

/**
 * Loi 25 / GDPR consent banner. Google Analytics only loads after the
 * visitor accepts; the choice is stored so the banner shows once.
 * Hidden entirely while no GA measurement id is configured, and never
 * rendered in the prerendered HTML (visibility is decided in an effect).
 */
export function CookieConsent() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(analyticsEnabled && getAnalyticsConsent() === null);
  }, []);

  if (!visible) return null;

  const isEnglish = pathname === "/en" || pathname.startsWith("/en/");

  const choose = (consent: "granted" | "denied") => {
    setAnalyticsConsent(consent);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={isEnglish ? "Cookie consent" : "Consentement aux témoins"}
      className="fixed bottom-4 inset-x-4 z-50 mx-auto max-w-xl rounded-xl border border-border bg-card p-4 shadow-lg sm:flex sm:items-center sm:gap-4"
    >
      <p className="text-sm text-muted-foreground">
        {isEnglish ? (
          <>
            We use cookies to improve your experience.{" "}
            <Link to="/en/privacy" className="underline hover:text-foreground">
              Learn more
            </Link>
          </>
        ) : (
          <>
            Nous utilisons des témoins pour améliorer votre expérience.{" "}
            <Link to="/privacy" className="underline hover:text-foreground">
              En savoir plus
            </Link>
          </>
        )}
      </p>
      <div className="mt-3 flex shrink-0 gap-2 sm:mt-0">
        <Button variant="outline" size="sm" onClick={() => choose("denied")}>
          {isEnglish ? "Decline" : "Refuser"}
        </Button>
        <Button size="sm" onClick={() => choose("granted")}>
          {isEnglish ? "Accept" : "Accepter"}
        </Button>
      </div>
    </div>
  );
}
